import debounceAction from 'debounce-action';
import { createSelector, createAction } from '@reduxjs/toolkit';

import { getApiConf } from './subscriptions.thunks';
import { hasFeature } from './datahub.thunks';

import { PipesAPI } from '../../api/pipes.api';
import { apologise } from './apology.thunks';
import { Json } from '../../types/json';

import { buildPipesDeletionPostActionsData } from '../../utils/pipes.utils';
import { getInternalDataEntities } from '../../utils/commonDataEntities.utils';
import { decoratePipe } from '../reducers/decoratePipe';
import { setInternalPipes } from '../reducers/internalPipes.reducer';

import { pipeDeletion } from '../../events/pipeDeletion.event';

import type {
  Pipe,
  PipeDeletedStatus,
  PipeFields,
  PipeResponse,
  PipeRuntime,
  PostPipesDeletionData,
} from '../../types/pipes.types';
import type { SomeObject, BatchDeletionResponse } from '../../types/common.types';
import type { AppThunk, RootState } from '../../types/state.types';

export const pipesSelector = createSelector(
  (state: RootState) => state.pipes,
  (pipes) => Object.values(pipes)
);
// -----------------------------------------------------------------------------

export const pipeAdded = createAction<PipeResponse>('pipe/added');
export const pipeUpdated = createAction<{ pipe: PipeResponse; oldPipe: Pipe }>('pipe/updated');

export const pipesAdded = createAction<{ pipes: PipeResponse[] }>('pipes/added');
export const pipesUpdated = createAction<{ pipes: PipeResponse[] }>('pipes/updated');
export const pipesDeleted = createAction<{ pipes: PipeResponse[] }>('pipes/deleted');

export const pipeRuntimeUpdated = createAction<{
  id: string;
  runtime: PipeRuntime;
}>('pipe/runtimeUpdated');
export const pipesRemoved = createAction<PostPipesDeletionData[]>('pipe/removed');
export const pipeLoaded = createAction<{ id: string; pipe: PipeResponse }>('pipe/loaded');
export const pipePartiallyLoaded = createAction<{ id: string; pipe: PipeResponse }>(
  'pipe/partiallyLoaded'
);
export const pipeLoading = createAction('pipe/loading');
export const pipeLoadingFailed = createAction('pipe/loadingFailed');
export const pipesLoadAllStarted = createAction('pipe/loadAllStarted');
export const pipesLoadAllFinished = createAction<PipeResponse[]>('pipe/loadAllFinished');
export const setPipesLoadingHasFinished = createAction('pipe/setPipesLoadingHasFinished');
export const pipesLoadAllFailed = createAction('pipe/loadAllFailed');
export const actionStatusSet = createAction<PipeDeletedStatus[]>('pipe/actionStatusSet');

export const PipeActions = {
  add(newPipe: Pipe): AppThunk {
    return function (dispatch, getState) {
      return PipesAPI.post(getApiConf(getState), newPipe).then((newPipes) =>
        dispatch(pipeAdded(newPipes[0]))
      );
    };
  },

  delete:
    (pipeIDs: string[]): AppThunk =>
    (dispatch, getState) => {
      const numberOfItems = pipeIDs.length;
      pipeDeletion.emit({ numberOfItems, status: 'start-api-delete' });

      return PipesAPI.remove(getApiConf(getState), pipeIDs)
        .then((batchDeletionResponse: BatchDeletionResponse) => {
          pipeDeletion.emit({ numberOfItems, status: 'finish-api-delete-request' });

          const pipes = getState().pipes;

          const isDeleted =
            batchDeletionResponse?.deleted && batchDeletionResponse?.deleted.length > 0;

          if (!isDeleted) {
            pipeDeletion.emit({ numberOfItems, status: 'deletion-failed' });
            dispatch(apologise('Delete selected pipes failed'));
          } else {
            // this code block runs post deletion actions, when pipes are fully or partially ('partially-deleted') deleted

            const { postDeleteBatchPipes, pipeActionStatus } = buildPipesDeletionPostActionsData(
              pipes,
              batchDeletionResponse?.deleted
            );

            pipeDeletion.emit({ numberOfItems, status: 'start-post-delete-actions' });

            dispatch(actionStatusSet(pipeActionStatus));
            dispatch(pipesRemoved(postDeleteBatchPipes));

            pipeDeletion.emit({ numberOfItems, status: 'finish-post-delete-actions' });
          }
        })
        .catch((error) => {
          dispatch(apologise(error));
          pipeDeletion.emit({ numberOfItems, status: 'finish-api-with-error' });
        });
    },

  runPumpOperation(id: string, operation: string, params: SomeObject): AppThunk {
    return function (dispatch, getState) {
      dispatch(
        actionStatusSet([
          {
            id,
            state: operation,
          },
        ])
      );
      return PipesAPI.runPumpOperation(getApiConf(getState), id, operation, params)
        .then((serverUpdate) => {
          if (!serverUpdate.runtime) {
            return dispatch(apologise('Unexpected server response: missing runtime'));
          }

          return dispatch(pipeRuntimeUpdated({ id, runtime: serverUpdate.runtime }));
        })
        .catch((error) => dispatch(apologise(error)));
    };
  },

  update(id: string, updatedPipe: Pipe): AppThunk {
    return function (dispatch, getState) {
      const oldPipe = getState().pipes[id];
      return PipesAPI.put(getApiConf(getState), id, updatedPipe)
        .then((pipe) => dispatch(pipeUpdated({ pipe, oldPipe })))
        .catch((error) => dispatch(apologise(error)));
    };
  },

  addPipes(updatedPipe: PipeResponse[]): AppThunk {
    return function (dispatch) {
      return dispatch(pipesAdded({ pipes: updatedPipe }));
    };
  },

  updatePipes(updatedPipe: PipeResponse[]): AppThunk {
    return function (dispatch) {
      return dispatch(pipesUpdated({ pipes: updatedPipe }));
    };
  },

  deletePipes(updatedPipe: PipeResponse[]): AppThunk {
    return function (dispatch) {
      return dispatch(pipesDeleted({ pipes: updatedPipe }));
    };
  },

  load: debounceAction(
    (id: string): AppThunk => {
      return function (dispatch, getState) {
        dispatch(pipeLoading());
        return PipesAPI.get(getApiConf(getState), id)
          .then((pipe) => dispatch(pipeLoaded({ id, pipe })))
          .catch((error) => {
            dispatch(apologise(error));
            dispatch(pipeLoadingFailed());
          });
      };
    },
    1000,
    { leading: true, trailing: false }
  ),

  partialLoad: debounceAction(
    (id: string, fields: PipeFields): AppThunk => {
      return function (dispatch, getState) {
        return PipesAPI.get(getApiConf(getState), id, fields)
          .then((pipe) => dispatch(pipePartiallyLoaded({ id, pipe })))
          .catch((error) => {
            dispatch(apologise(error));
            dispatch(pipeLoadingFailed());
          });
      };
    },
    1000,
    { leading: true, trailing: false }
  ),

  loadForced: (id: string): AppThunk => {
    return function (dispatch, getState) {
      return PipesAPI.get(getApiConf(getState), id)
        .then((pipe) => dispatch(pipeLoaded({ id, pipe })))
        .catch((error) => dispatch(apologise(error)));
    };
  },

  loadAll: debounceAction(
    (loadInternal = false): AppThunk => {
      return function (dispatch, getState) {
        dispatch(pipesLoadAllStarted());
        return PipesAPI.getAll(getApiConf(getState), loadInternal)
          .then((allPipes) => {
            dispatch(pipesLoadAllFinished(allPipes));

            if (loadInternal) {
              const filtered = getInternalDataEntities<PipeResponse>(allPipes).map(
                (p: PipeResponse) => decoratePipe(p)
              );

              dispatch(setInternalPipes(filtered));
            }
          })
          .catch((error) => {
            dispatch(pipesLoadAllFailed());
            dispatch(apologise(error));
          });
      };
    },
    1000,
    { leading: true, trailing: false }
  ),

  loadAllForced: (loadInternal = false): AppThunk => {
    return function (dispatch, getState) {
      dispatch(pipesLoadAllStarted());
      return PipesAPI.getAll(getApiConf(getState), loadInternal)
        .then((pipes) => dispatch(pipesLoadAllFinished(pipes)))
        .catch((error) => {
          dispatch(pipesLoadAllFailed());
          dispatch(apologise(error));
        });
    };
  },

  analysePipe(pipe: Pipe): AppThunk {
    return function (dispatch, getState) {
      return PipesAPI.analyse(getApiConf(getState), pipe, hasFeature(getState)(1));
    };
  },

  previewPipe(pipe: Pipe, customSourceEntity: Json, useTrace = false): AppThunk {
    return function (dispatch, getState) {
      return PipesAPI.preview(
        getApiConf(getState),
        pipe,
        customSourceEntity,
        useTrace,
        hasFeature(getState)(1)
      );
    };
  },
};
